import types from '../mutation-types';
import DataAssistantReportsAPI from '../../api/dataAssistantReports';
import DataAssistantReportMembersAPI from '../../api/dataAssistantReportMembers';

export const state = {
  reports: [],
  currentReport: null,
  items: [],
  itemResults: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getReports: _state => _state.reports,
  getCurrentReport: _state => _state.currentReport,
  getItems: _state => _state.items,
  getItemResults: _state => _state.itemResults,
  getUIFlags: _state => _state.uiFlags,
};

export const actions = {
  fetchReports: async ({ commit }) => {
    commit(types.SET_DA_REPORTS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await DataAssistantReportsAPI.get();
      commit(types.SET_DA_REPORTS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_DA_REPORTS_UI_FLAG, { isFetching: false });
    }
  },

  fetchReport: async ({ commit, dispatch }, id) => {
    commit(types.SET_DA_REPORTS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await DataAssistantReportsAPI.show(id);
      commit(types.SET_DA_CURRENT_REPORT, data);
      commit(types.SET_DA_REPORT_ITEMS, data.items || []);
      dispatch('runAllItems', id);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_DA_REPORTS_UI_FLAG, { isFetching: false });
    }
  },

  createReport: async ({ commit }, { name }) => {
    commit(types.SET_DA_REPORTS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await DataAssistantReportsAPI.create({ name });
      commit(types.ADD_DA_REPORT, data);
      return data;
    } finally {
      commit(types.SET_DA_REPORTS_UI_FLAG, { isCreating: false });
    }
  },

  updateReport: async ({ commit }, { id, name }) => {
    const { data } = await DataAssistantReportsAPI.update(id, { name });
    commit(types.UPDATE_DA_REPORT, data);
    return data;
  },

  moveReport: async ({ commit }, { id, direction }) => {
    const { data } = await DataAssistantReportsAPI.moveReport(id, direction);
    commit(types.SET_DA_REPORTS, data);
    return data;
  },

  deleteReport: async ({ commit }, id) => {
    await DataAssistantReportsAPI.delete(id);
    commit(types.DELETE_DA_REPORT, id);
  },

  addItem: async ({ commit }, { reportId, message_id }) => {
    const { data } = await DataAssistantReportsAPI.addItem(reportId, {
      message_id,
    });
    commit(types.ADD_DA_REPORT_ITEM, data);
    return data;
  },

  updateItem: async ({ commit }, { reportId, itemId, ...attrs }) => {
    const { data } = await DataAssistantReportsAPI.updateItem(
      reportId,
      itemId,
      attrs
    );
    commit(types.UPDATE_DA_REPORT_ITEM, data);
    return data;
  },

  moveItem: async ({ commit }, { reportId, itemId, direction }) => {
    const { data } = await DataAssistantReportsAPI.moveItem(
      reportId,
      itemId,
      direction
    );
    commit(types.REORDER_DA_REPORT_ITEMS, data);
    return data;
  },

  removeItem: async ({ commit }, { reportId, itemId }) => {
    await DataAssistantReportsAPI.removeItem(reportId, itemId);
    commit(types.DELETE_DA_REPORT_ITEM, itemId);
  },

  runItem: async ({ commit }, { reportId, itemId, forceRefresh = false }) => {
    commit(types.SET_DA_ITEM_RESULT, {
      itemId,
      result: { loading: true, result_data: null, error: null },
    });
    try {
      const { data } = await DataAssistantReportsAPI.runItem(
        reportId,
        itemId,
        forceRefresh
      );
      commit(types.SET_DA_ITEM_RESULT, {
        itemId,
        result: { loading: false, result_data: data.result_data, error: null },
      });
    } catch (error) {
      const message =
        error?.response?.data?.error || error.message || 'Query failed';
      commit(types.SET_DA_ITEM_RESULT, {
        itemId,
        result: { loading: false, result_data: null, error: message },
      });
    }
  },

  runAllItems: ({ state: _state, dispatch }, reportId) => {
    _state.items.forEach(item => {
      dispatch('runItem', { reportId, itemId: item.id });
    });
  },

  fetchReportCollaborators: async ({ commit }, reportId) => {
    const { data } = await DataAssistantReportMembersAPI.show(reportId);
    commit(types.SET_DA_REPORT_COLLABORATORS, { reportId, ...data });
    return data;
  },

  updateReportCollaborators: async (
    { commit },
    { reportId, userIds, teamIds }
  ) => {
    const { data } = await DataAssistantReportMembersAPI.update(reportId, {
      userIds,
      teamIds,
    });
    commit(types.SET_DA_REPORT_COLLABORATORS, { reportId, ...data });
    return data;
  },
};

export const mutations = {
  [types.SET_DA_REPORTS_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [types.SET_DA_REPORTS](_state, data) {
    _state.reports = data;
  },
  [types.ADD_DA_REPORT](_state, data) {
    _state.reports.unshift(data);
  },
  [types.UPDATE_DA_REPORT](_state, data) {
    _state.reports = _state.reports.map(r => (r.id === data.id ? data : r));
    if (_state.currentReport?.id === data.id) {
      _state.currentReport = { ..._state.currentReport, ...data };
    }
  },
  [types.DELETE_DA_REPORT](_state, id) {
    _state.reports = _state.reports.filter(r => r.id !== id);
    if (_state.currentReport?.id === id) {
      _state.currentReport = null;
      _state.items = [];
      _state.itemResults = {};
    }
  },
  [types.SET_DA_CURRENT_REPORT](_state, data) {
    _state.currentReport = data;
  },
  [types.SET_DA_REPORT_ITEMS](_state, data) {
    _state.items = data;
    _state.itemResults = {};
  },
  [types.ADD_DA_REPORT_ITEM](_state, data) {
    _state.items.push(data);
  },
  [types.UPDATE_DA_REPORT_ITEM](_state, data) {
    _state.items = _state.items.map(i => (i.id === data.id ? data : i));
  },
  [types.DELETE_DA_REPORT_ITEM](_state, id) {
    _state.items = _state.items.filter(i => i.id !== id);
    const newResults = { ..._state.itemResults };
    delete newResults[id];
    _state.itemResults = newResults;
  },
  [types.SET_DA_ITEM_RESULT](_state, { itemId, result }) {
    _state.itemResults = { ..._state.itemResults, [itemId]: result };
  },
  [types.REORDER_DA_REPORT_ITEMS](_state, data) {
    _state.items = data;
  },
  [types.SET_DA_REPORT_COLLABORATORS](
    _state,
    { reportId, user_ids, team_ids }
  ) {
    if (_state.currentReport?.id === reportId) {
      _state.currentReport = {
        ..._state.currentReport,
        user_ids: user_ids || [],
        team_ids: team_ids || [],
      };
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
